import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeError, setSubscribeError] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setSubscribeStatus('success');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
      return;
    }

    setSubscribeStatus('loading');
    setSubscribeError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d99og2nk7gok24d4af8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        form.reset();
        setTimeout(() => setSubscribeStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Something went wrong.';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setSubscribeStatus('error');
          setSubscribeError('Your submission could not be processed. Please try again.');
        } else {
          setSubscribeStatus('error');
          setSubscribeError(serverMsg);
        }
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeError('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50 rounded-t-[32px] md:rounded-t-[40px] mx-4 md:mx-6 mt-20">
      <div className="px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-3">
              Join Superbuy Spread
            </h3>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6 max-w-md">
              Stay updated with the latest Superbuy spreadsheet finds, organized product collections, and smart shopping guides.
            </p>
            <form
              data-readdy-form="newsletter-footer"
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                className="flex-1 bg-transparent border-b border-foreground-700 focus:border-background-50 outline-none text-sm py-2.5 text-background-50 placeholder:text-foreground-500 transition-colors"
                required
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'loading' || subscribeStatus === 'success'}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 text-foreground-950 text-sm font-medium rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {subscribeStatus === 'loading' ? (
                  'Sending...'
                ) : subscribeStatus === 'success' ? (
                  <>
                    Subscribed!
                    <i className="ri-check-line text-sm"></i>
                  </>
                ) : (
                  <>
                    Subscribe
                    <i className="ri-arrow-right-line text-sm"></i>
                  </>
                )}
              </button>
            </form>
            {subscribeStatus === 'error' && subscribeError && (
              <p className="text-xs text-red-400 mt-2">{subscribeError}</p>
            )}
            {subscribeStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">Thank you for subscribing!</p>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground-400 mb-4">
              Quick Links
            </h4>
            <div className="flex flex-col gap-3">
              <Link
                to="/"
                className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 underline decoration-foreground-700/50 hover:decoration-background-50"
              >
                Home
              </Link>
              <Link
                to="/superbuy-spreadsheet-guide"
                className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 underline decoration-foreground-700/50 hover:decoration-background-50"
              >
                Complete Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 underline decoration-foreground-700/50 hover:decoration-background-50 cursor-pointer"
              >
                Main Website
              </a>
              <span className="text-sm text-foreground-300 underline decoration-foreground-700/50">
                Categories
              </span>
            </div>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground-400 mb-4">
              Top Categories
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200 underline decoration-foreground-700/50 hover:decoration-background-50 cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-foreground-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700/50"
            >
              Twitter
            </a>
            <a
              href="#"
              className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700/50"
            >
              Pinterest
            </a>
            <a
              href="#"
              className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700/50"
            >
              Reddit
            </a>
          </div>
          <p className="text-sm text-foreground-500">
            &copy; {new Date().getFullYear()} Superbuy Spread. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700/50"
            >
              Privacy
            </a>
            <a
              href="#"
              className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700/50"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}